import { getWidgetDefinitions } from '@dixa/lib-integrations-partner-widgets';
import { IconNames } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import type { CardType } from 'lib-shared-types-api';

import { PartnerCardType } from 'apis/partnerIntegrations';
import { FeatureFlagName } from 'utils/featureFlagging/featureFlags';
import { AppPathWithoutParams } from 'utils/url/appPaths';

export type IntegrationType = 'customCard' | 'partnerWidget';

export type Integration = {
  readonly id: string;
  readonly icon: IconNames;
  readonly iconColors: {
    readonly backgroundColor: string;
    readonly contentColor: string;
  };
  readonly label: string;
  readonly path?: AppPathWithoutParams;
  readonly integrationType?: IntegrationType;
  readonly description: string;
  readonly featureFlag?: FeatureFlagName;
};

const widgetDefinitions = getWidgetDefinitions();

export const customCardIntegrations: Record<CardType, Integration> = {
  custom: {
    id: 'custom',
    icon: 'code',
    iconColors: {
      contentColor: 'rgb(177, 177, 177)',
      backgroundColor: 'rgb(177, 177, 177, 0.1)',
    },
    label: 'Custom card',
    path: '/settings/integrations/custom-card/create',
    integrationType: 'customCard',
    description: 'Embeds custom data in the conversation sidebar.',
  },
  shopify: {
    id: 'shopify',
    icon: 'shopify',
    iconColors: {
      contentColor: 'rgb(177, 177, 177)',
      backgroundColor: 'rgb(177, 177, 177, 0.1)',
    },
    label: 'Shopify (deprecated)',
    path: '/settings/integrations/shopify-card/create',
    integrationType: 'customCard',
    description:
      'The support for this Shopify card is deprecated. Please use the new "Shopify" integration instead.',
    featureFlag: 'ecosystem-legacy-shopify-card-creation',
  },
  magento: {
    id: 'magento',
    icon: 'magento',
    iconColors: {
      contentColor: 'rgb(193, 72, 0)',
      backgroundColor: 'rgb(193, 72, 0, 0.1)',
    },
    label: 'Magento',
    path: '/settings/integrations/magento-card/create',
    integrationType: 'customCard',
    description:
      'Embeds Magento data in the conversation sidebar. Easy way to get customers order info from Magento by email or phone number.',
  },
  salesforce: {
    id: 'salesforce',
    icon: 'salesforce',
    iconColors: {
      contentColor: 'rgb(24, 168, 216)',
      backgroundColor: 'rgb(24, 168, 216, 0.1)',
    },
    label: 'Salesforce',
    path: '/settings/integrations/salesforce-card/create',
    integrationType: 'customCard',
    description: 'Embeds Salesforce data in the conversation sidebar.',
  },
};

export const partnerIntegrations: Record<PartnerCardType, Integration> = {
  GITHUB: {
    id: 'github',
    icon: widgetDefinitions.GITHUB.icon,
    iconColors: widgetDefinitions.GITHUB.iconColors,
    label: 'Github',
    integrationType: 'partnerWidget',
    description: 'Configure your Github widget.',
  },
  SHOPIFY: {
    id: 'shopifyV2',
    icon: widgetDefinitions.SHOPIFY.icon,
    iconColors: widgetDefinitions.SHOPIFY.iconColors,
    label: 'Shopify',
    integrationType: 'partnerWidget',
    description: 'Configure your Shopify widget.',
  },
  JIRA: {
    id: 'jira',
    icon: widgetDefinitions.JIRA.icon,
    iconColors: widgetDefinitions.JIRA.iconColors,
    label: 'Jira',
    integrationType: 'partnerWidget',
    description: 'Configure your Jira widget.',
  },
  MIUROS: {
    id: 'miuros',
    icon: widgetDefinitions.MIUROS.icon,
    iconColors: widgetDefinitions.MIUROS.iconColors,
    label: 'Miuros',
    integrationType: 'partnerWidget',
    description: 'Configure your Miuros widget.',
  },
  LINEAR: {
    id: 'linear',
    icon: widgetDefinitions.LINEAR.icon,
    iconColors: widgetDefinitions.LINEAR.iconColors,
    label: 'Linear',
    integrationType: 'partnerWidget',
    description: 'Configure your Linear widget.',
  },
  KLAVIYO: {
    id: 'klaviyo',
    icon: widgetDefinitions.KLAVIYO.icon,
    iconColors: widgetDefinitions.KLAVIYO.iconColors,
    label: 'Klaviyo',
    integrationType: 'partnerWidget',
    description: 'Configure your Klaviyo widget.',
  },
  VOYADO: {
    id: 'voyado',
    icon: widgetDefinitions.VOYADO.icon,
    iconColors: widgetDefinitions.VOYADO.iconColors,
    label: 'Voyado',
    integrationType: 'partnerWidget',
    description: 'Configure your Voyado widget.',
  },
  LOYALTYLION: {
    id: 'loyaltylion',
    icon: widgetDefinitions.LOYALTYLION.icon,
    iconColors: widgetDefinitions.LOYALTYLION.iconColors,
    label: 'LoyaltyLion',
    integrationType: 'partnerWidget',
    description: 'Configure your LoyaltyLion widget.',
  },
  RECHARGE: {
    id: 'recharge',
    icon: widgetDefinitions.RECHARGE.icon,
    iconColors: widgetDefinitions.RECHARGE.iconColors,
    label: 'Recharge',
    integrationType: 'partnerWidget',
    description: 'Configure your Recharge widget.',
  },
  BRAZE: {
    id: 'braze',
    icon: widgetDefinitions.BRAZE.icon,
    iconColors: widgetDefinitions.BRAZE.iconColors,
    label: 'Braze',
    integrationType: 'partnerWidget',
    description: 'Configure your Braze widget.',
  },
};

const unknownIntegration: Integration = {
  id: 'unknown',
  icon: 'question',
  iconColors: {
    backgroundColor: colors.neutral['000'],
    contentColor: colors.neutral['300'],
  },
  label: 'Unknown',
  path: '/settings/integrations',
  description: 'This widget is not supported yet.',
};

export const integrations: Record<PartnerCardType | CardType, Integration> = {
  ...partnerIntegrations,
  ...customCardIntegrations,
};

type GetIntegrationProps = {
  id: string;
};

export const getIntegration = ({ id }: GetIntegrationProps): Integration => {
  if (id in integrations) {
    return integrations[id];
  }

  return unknownIntegration;
};
